const WEEKDAY_NAMES = {
  '0': 'Sunday',
  '1': 'Monday',
  '2': 'Tuesday',
  '3': 'Wednesday',
  '4': 'Thursday',
  '5': 'Friday',
  '6': 'Saturday',
  '7': 'Sunday'
};

const MONTH_NAMES = {
  '1': 'January',
  '2': 'February',
  '3': 'March',
  '4': 'April',
  '5': 'May',
  '6': 'June',
  '7': 'July',
  '8': 'August',
  '9': 'September',
  '10': 'October',
  '11': 'November',
  '12': 'December'
};

function pad2(value) {
  return String(value).padStart(2, '0');
}

function to12Hour(hourValue) {
  const hour = Number(hourValue);
  if (!Number.isInteger(hour) || hour < 0 || hour > 23) {
    return null;
  }

  const period = hour >= 12 ? 'PM' : 'AM';
  const normalized = hour % 12 || 12;
  return `${normalized}:${pad2(0)} ${period}`;
}

function formatTime(hourValue, minuteValue) {
  const hour = Number(hourValue);
  const minute = Number(minuteValue);
  if (!Number.isInteger(hour) || !Number.isInteger(minute) || hour < 0 || hour > 23 || minute < 0 || minute > 59) {
    return null;
  }

  const period = hour >= 12 ? 'PM' : 'AM';
  const normalizedHour = hour % 12 || 12;
  return `${normalizedHour}:${pad2(minute)} ${period}`;
}

function isNumberToken(token) {
  return /^\d+$/.test(token);
}

function isWildcard(token) {
  return token === '*';
}

function parseStep(token) {
  const match = token.match(/^(?:\*|0)\/(\d+)$/);
  return match ? Number(match[1]) : null;
}

function parseList(token) {
  return token.includes(',') ? token.split(',').map((part) => part.trim()).filter(Boolean) : null;
}

function parseRange(token) {
  const match = token.match(/^(\d+)-(\d+)$/);
  return match ? [match[1], match[2]] : null;
}

function ordinal(value) {
  const number = Number(value);
  if (!Number.isInteger(number)) {
    return String(value);
  }

  const mod10 = number % 10;
  const mod100 = number % 100;
  if (mod10 === 1 && mod100 !== 11) return `${number}st`;
  if (mod10 === 2 && mod100 !== 12) return `${number}nd`;
  if (mod10 === 3 && mod100 !== 13) return `${number}rd`;
  return `${number}th`;
}

function joinWords(values) {
  if (!values.length) return '';
  if (values.length === 1) return values[0];
  if (values.length === 2) return `${values[0]} and ${values[1]}`;
  return `${values.slice(0, -1).join(', ')}, and ${values.at(-1)}`;
}

function formatNamedValues(token, namesMap) {
  const list = parseList(token);
  if (list) {
    return joinWords(list.map((part) => namesMap[part] || part));
  }

  const range = parseRange(token);
  if (range) {
    return `${namesMap[range[0]] || range[0]} through ${namesMap[range[1]] || range[1]}`;
  }

  return namesMap[token] || token;
}

function describeMinuteToken(minute) {
  if (isWildcard(minute)) return 'every minute';
  const step = parseStep(minute);
  if (step) return `every ${step} minutes`;
  if (isNumberToken(minute)) return `at minute ${pad2(minute)}`;
  const list = parseList(minute);
  if (list) return `at minutes ${list.map(pad2).join(', ')}`;
  const range = parseRange(minute);
  if (range) return `each minute from ${pad2(range[0])} through ${pad2(range[1])}`;
  return `minute field "${minute}"`;
}

function describeHourToken(hour, minute) {
  if (isWildcard(hour)) {
    return isNumberToken(minute) ? `every hour at :${pad2(minute)}` : 'every hour';
  }

  const step = parseStep(hour);
  if (step) {
    return isNumberToken(minute)
      ? `every ${step} hours at :${pad2(minute)}`
      : `every ${step} hours`;
  }

  if (isNumberToken(hour)) {
    if (isNumberToken(minute)) {
      return `at ${formatTime(hour, minute)}`;
    }
    return `during the ${to12Hour(hour) || hour} hour`;
  }

  const list = parseList(hour);
  if (list) {
    return `during hours ${list.join(', ')}`;
  }

  const range = parseRange(hour);
  if (range) {
    return `between hours ${range[0]} and ${range[1]}`;
  }

  return `hour field "${hour}"`;
}

function describeDayOfMonthToken(day) {
  if (isWildcard(day)) return '';
  const step = parseStep(day);
  if (step) return `every ${step} days of the month`;
  if (isNumberToken(day)) return `on day ${ordinal(day)}`;
  const list = parseList(day);
  if (list) return `on days ${list.map(ordinal).join(', ')}`;
  const range = parseRange(day);
  if (range) return `on days ${ordinal(range[0])} through ${ordinal(range[1])}`;
  return `day-of-month field "${day}"`;
}

function describeMonthToken(month) {
  if (isWildcard(month)) return '';
  const step = parseStep(month);
  if (step) return `every ${step} months`;
  if (isNumberToken(month)) return `in ${MONTH_NAMES[month] || `month ${month}`}`;
  const list = parseList(month);
  if (list) return `in ${joinWords(list.map((part) => MONTH_NAMES[part] || `month ${part}`))}`;
  const range = parseRange(month);
  if (range) return `from ${MONTH_NAMES[range[0]] || range[0]} through ${MONTH_NAMES[range[1]] || range[1]}`;
  return `month field "${month}"`;
}

function describeWeekdayToken(weekday) {
  if (isWildcard(weekday)) return '';
  const step = parseStep(weekday);
  if (step) return `every ${step} weekdays`;
  return `on ${formatNamedValues(weekday, WEEKDAY_NAMES)}`;
}

export function describeCronExpression(cron) {
  const normalized = String(cron || '').trim().replace(/\s+/g, ' ');
  if (!normalized) {
    return 'Enter a 5-part cron expression';
  }

  const parts = normalized.split(' ');
  if (parts.length !== 5) {
    return 'Invalid cron format';
  }

  const [minute, hour, dayOfMonth, month, weekday] = parts;

  if (normalized === '* * * * *') return 'Every minute';

  const minuteStep = parseStep(minute);
  const hourStep = parseStep(hour);

  if (minuteStep && isWildcard(hour) && isWildcard(dayOfMonth) && isWildcard(month) && isWildcard(weekday)) {
    return `Every ${minuteStep} minutes`;
  }

  if (isNumberToken(minute) && isWildcard(hour) && isWildcard(dayOfMonth) && isWildcard(month) && isWildcard(weekday)) {
    return `Every hour at :${pad2(minute)}`;
  }

  if (isNumberToken(minute) && hourStep && isWildcard(dayOfMonth) && isWildcard(month) && isWildcard(weekday)) {
    return `Every ${hourStep} hours at :${pad2(minute)}`;
  }

  if (isNumberToken(minute) && isNumberToken(hour) && isWildcard(dayOfMonth) && isWildcard(month) && isWildcard(weekday)) {
    return `Daily at ${formatTime(hour, minute)}`;
  }

  if (isNumberToken(minute) && isNumberToken(hour) && isWildcard(dayOfMonth) && isWildcard(month) && !isWildcard(weekday)) {
    return `Every ${formatNamedValues(weekday, WEEKDAY_NAMES)} at ${formatTime(hour, minute)}`;
  }

  if (isNumberToken(minute) && isNumberToken(hour) && !isWildcard(dayOfMonth) && isWildcard(month) && isWildcard(weekday)) {
    return `Monthly on day ${ordinal(dayOfMonth)} at ${formatTime(hour, minute)}`;
  }

  if (isNumberToken(minute) && isNumberToken(hour) && !isWildcard(dayOfMonth) && !isWildcard(month) && isWildcard(weekday)) {
    return `Yearly on ${MONTH_NAMES[month] || `month ${month}`} ${ordinal(dayOfMonth)} at ${formatTime(hour, minute)}`;
  }

  const clauses = [
    describeMinuteToken(minute),
    describeHourToken(hour, minute),
    describeDayOfMonthToken(dayOfMonth),
    describeMonthToken(month),
    describeWeekdayToken(weekday)
  ].filter(Boolean);

  if (!clauses.length) {
    return 'Custom schedule';
  }

  const [first, ...rest] = clauses;
  const sentence = [first.charAt(0).toUpperCase() + first.slice(1), ...rest].join(', ');
  return sentence;
}

export const CRON_HELP_LINES = [
  'Cron format uses 5 fields: minute hour day-of-month month weekday.',
  'Use * to mean any value.',
  'Use */n to repeat every n units, for example */15 for every 15 minutes.',
  'Use commas for lists, for example 0,15,30,45.',
  'Use ranges like 1-5 for Monday through Friday in the weekday field.',
  'Weekday values are 0-6, and 7 is also accepted for Sunday.'
];

export const CRON_HELP_EXAMPLES = [
  { cron: '*/30 * * * *', description: 'Every 30 minutes' },
  { cron: '0 * * * *', description: 'Every hour at :00' },
  { cron: '15 */6 * * *', description: 'Every 6 hours at :15' },
  { cron: '0 9 * * 1-5', description: 'Every Monday through Friday at 9:00 AM' },
  { cron: '0 0 * * *', description: 'Daily at 12:00 AM' },
  { cron: '0 8 1 * *', description: 'Monthly on day 1 at 8:00 AM' }
];
